import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, CheckCircle, XCircle, Clock, RefreshCw,
  Calendar, AlertCircle, Info,
} from 'lucide-react';
import { hrApi } from '../../../api';
import { getOrgId } from '../../utils/auth';
import { useMasterData } from '../../../../utils/useMasterData';

// ─── Tiny helpers ──────────────────────────────────────────────────────────────
const orgId = () => getOrgId();
const BASE   = () => `/orgs/${orgId()}/leave`;

const normalizePersonRecord = (item) => {
  if (!item) return null;
  const dbId = item.db_id ?? item.id ?? '';
  const firstName = item.firstName ?? item.first_name ?? '';
  const lastName = item.lastName ?? item.last_name ?? '';
  const fullName = item.name || `${firstName} ${lastName}`.trim() || String(item.id || '');
  return {
    ...item,
    original_id: item.id,
    id: String(dbId),
    db_id: dbId,
    firstName,
    lastName,
    first_name: firstName,
    last_name: lastName,
    name: fullName,
  };
};

const staffOptionLabel = (item) => {
  const parts = [
    item?.name,
    item?.role && item.role !== 'UNASSIGNED' ? item.role : null,
    item?.email || null,
  ].filter(Boolean);
  return parts.join(' - ');
};

const workerOptionLabel = (item) => {
  const parts = [
    item?.name,
    item?.worker_code || item?.employee_code || item?.legacy_code || item?.code || item?.original_id || item?.public_id || item?.display_id || item?.item_id || item?.staff_code || item?.id,
    item?.role || null,
  ].filter(Boolean);
  return parts.join(' - ');
};

// Inline spinner — shown during any API call so users know to wait
const Spinner = ({ size = 16, color = 'currentColor' }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke={color} strokeWidth={2.5} strokeLinecap="round"
    style={{ animation: 'leave-spin .7s linear infinite', flexShrink: 0 }}
    aria-label="Loading"
  >
    <circle cx="12" cy="12" r="10" strokeOpacity={0.25} />
    <path d="M12 2 A10 10 0 0 1 22 12" />
  </svg>
);
// Inject keyframe once
if (typeof document !== 'undefined' && !document.getElementById('leave-spin-style')) {
  const s = document.createElement('style');
  s.id = 'leave-spin-style';
  s.textContent = '@keyframes leave-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(s);
}

const Shimmer = ({ width = '100%', height = 16, radius = 6, style = {} }) => (
  <span className="skeleton" style={{ width, height, borderRadius: radius, display: 'inline-block', ...style }} />
);

const TableRowSkeleton = ({ cols }) => (
  <tr>
    {Array.from({ length: cols }).map((_, i) => (
      <td key={i} style={{ padding: '12px 14px' }}>
        <Shimmer height={14} width={i === 0 ? '80%' : '60%'} />
      </td>
    ))}
  </tr>
);

// Status badge
const StatusBadge = ({ status }) => {
  const MAP = {
    pending:  { bg: '#fef9c3', color: '#854d0e', label: 'Pending',  icon: <Clock size={12} /> },
    approved: { bg: '#dcfce7', color: '#166534', label: 'Approved', icon: <CheckCircle size={12} /> },
    rejected: { bg: '#fee2e2', color: '#991b1b', label: 'Rejected', icon: <XCircle size={12} /> },
  };
  const cfg = MAP[status] || MAP.pending;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
      background: cfg.bg, color: cfg.color,
    }}>
      {cfg.icon} {cfg.label}
    </span>
  );
};

// Compact usage bar for balance tab
const UsageBar = ({ used, allocated }) => {
  const pct = allocated > 0 ? Math.min(100, (used / allocated) * 100) : 0;
  const remaining = Math.max(0, allocated - used);
  const color = pct < 50 ? '#22c55e' : pct < 80 ? '#f59e0b' : '#ef4444';
  return (
    <div style={{ minWidth: 100 }}>
      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 3 }}>
        {used}/{allocated} d &nbsp;
        <span style={{ color, fontWeight: 700 }}>{remaining} left</span>
      </div>
      <div style={{ height: 6, background: '#e2e8f0', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 4, transition: 'width .4s' }} />
      </div>
    </div>
  );
};

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function LeaveScreen({ addToast, userRole, canWrite: canWriteProp }) {
  const { getOptions } = useMasterData('LEAVE_TYPE');
  const defaultLeaveTypes = getOptions('LEAVE_TYPE').map((option) => ({
    id: option.value,
    code: option.value,
    name: option.label,
    days_allowed: Number(option.metadata?.days_allowed ?? 0),
    is_paid: Boolean(option.metadata?.is_paid),
    carry_forward: Boolean(option.metadata?.carry_forward),
  }));
  const [tab, setTab] = useState('requests');

  // ── Requests tab ─────────────────────────────────────────────
  const [requests, setRequests]       = useState([]);
  const [reqLoading, setReqLoading]   = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  // ── Balances tab ──────────────────────────────────────────────
  const [summary, setSummary]         = useState([]);
  const [leaveTypes, setLeaveTypes]   = useState([]);
  const [balLoading, setBalLoading]   = useState(false);
  const [balYear, setBalYear]         = useState(new Date().getFullYear());

  // ── Leave Types tab ───────────────────────────────────────────
  const [ltypes, setLtypes]           = useState([]);
  const [ltLoading, setLtLoading]     = useState(false);
  const [ltForm, setLtForm]           = useState({ name: '', code: '', days_allowed: 12, is_paid: true, carry_forward: false });
  const [ltSaving, setLtSaving]       = useState(false);
  const [showLtForm, setShowLtForm]   = useState(false);

  // ── Apply Leave modal ─────────────────────────────────────────
  const [showApply, setShowApply]     = useState(false);
  const [applyForm, setApplyForm]     = useState({
    entity_type: 'employee', employee_id: '', worker_id: '',
    leave_type_id: '', from_date: '', to_date: '', reason: '',
  });
  const [applyWorkers, setApplyWorkers] = useState([]);
  const [applyEmployees, setApplyEmployees] = useState([]);
  const [applyLeaveTypes, setApplyLeaveTypes] = useState([]);
  const [applySaving, setApplySaving] = useState(false);

  // ── Action modal ──────────────────────────────────────────────
  const [actionTarget, setActionTarget] = useState(null); // { req, action }
  const [actionRemarks, setActionRemarks] = useState('');
  const [actionSaving, setActionSaving] = useState(false);

  // ─── Fetch helpers ──────────────────────────────────────────────────────────
  const fetchRequests = useCallback(async () => {
    setReqLoading(true);
    try {
      const r = await hrApi.get(`${BASE()}/requests?status=${statusFilter}`);
      setRequests(r.data.data || []);
    } catch {
      addToast?.('Failed to load leave requests', 'error');
    } finally {
      setReqLoading(false);
    }
  }, [addToast, statusFilter]);

  const fetchSummary = useCallback(async () => {
    setBalLoading(true);
    try {
      const r = await hrApi.get(`${BASE()}/summary?year=${balYear}`);
      setSummary(r.data.data || []);
      setLeaveTypes(r.data.leave_types || []);
    } catch {
      addToast?.('Failed to load leave summary', 'error');
    } finally {
      setBalLoading(false);
    }
  }, [addToast, balYear]);

  const fetchLeaveTypes = useCallback(async () => {
    setLtLoading(true);
    try {
      const r = await hrApi.get(`${BASE()}/types`);
      const types = Array.isArray(r.data?.data) && r.data.data.length ? r.data.data : defaultLeaveTypes;
      setLtypes(types);
      setApplyLeaveTypes(types);
    } catch {
      setLtypes(defaultLeaveTypes);
      setApplyLeaveTypes(defaultLeaveTypes);
      addToast?.('Leave types unavailable, using default leave options', 'info');
    } finally {
      setLtLoading(false);
    }
  }, [addToast, defaultLeaveTypes]);

  // Fetch workers + employees for Apply modal
  const fetchPeople = useCallback(async () => {
    const oid = orgId();
    try {
      const [wRes, eRes] = await Promise.allSettled([
        hrApi.get(`/orgs/${oid}/employees`),
        hrApi.get(`/orgs/${oid}/employees`, { params: { type: 'Staff' } }),
      ]);
      if (wRes.status === 'fulfilled') {
        const workerRows = (wRes.value.data?.items || wRes.value.data?.data || []).map(normalizePersonRecord).filter(Boolean);
        setApplyWorkers(workerRows);
      }
      if (eRes.status === 'fulfilled') {
        const employeeRows = (eRes.value.data?.items || eRes.value.data?.data || []).map(normalizePersonRecord).filter(Boolean);
        setApplyEmployees(employeeRows);
      }
    } catch { /* non-critical */ }
  }, []);

  // ─── Tab-level effects ──────────────────────────────────────────────────────

  // ─── Requests tab: re-fetch when filter changes ─────────────────────────────
  useEffect(() => { if (tab === 'requests') fetchRequests(); }, [fetchRequests, statusFilter, tab]);

  // ─── Apply leave ────────────────────────────────────────────────────────────
  const openApply = async () => {
    setApplyForm({ entity_type: 'employee', employee_id: '', worker_id: '', leave_type_id: '', from_date: '', to_date: '', reason: '' });
    await Promise.all([fetchLeaveTypes(), fetchPeople()]);
    setShowApply(true);
  };

  const submitApply = async () => {
    if (!applyForm.leave_type_id) return addToast?.('Select a leave type', 'error');
    if (!applyForm.from_date || !applyForm.to_date) return addToast?.('Select dates', 'error');
    if (applyForm.entity_type === 'employee' && !applyForm.employee_id) return addToast?.('Select employee', 'error');
    if (applyForm.entity_type === 'worker' && !applyForm.worker_id) return addToast?.('Select worker', 'error');

    setApplySaving(true);
    try {
      const selectedLeaveType = applyLeaveTypes.find((lt) => String(lt.id) === String(applyForm.leave_type_id));
      const fallbackLeaveCode = String(selectedLeaveType?.code || applyForm.leave_type_id || '').trim().toUpperCase();
      const payload = {
        leave_type_id: selectedLeaveType && /^\d+$/.test(String(selectedLeaveType.id)) ? Number(selectedLeaveType.id) : undefined,
        leave_type_code: fallbackLeaveCode || undefined,
        leave_type_name: selectedLeaveType?.name,
        days_allowed: selectedLeaveType?.days_allowed,
        is_paid: selectedLeaveType?.is_paid,
        carry_forward: selectedLeaveType?.carry_forward,
        from_date: applyForm.from_date,
        to_date: applyForm.to_date,
        reason: applyForm.reason,
        entity_type: applyForm.entity_type,
        employee_id: applyForm.entity_type === 'employee' ? applyForm.employee_id : undefined,
        worker_id:   applyForm.entity_type === 'worker'   ? Number(applyForm.worker_id) : undefined,
      };
      const r = await hrApi.post(`${BASE()}/requests`, payload);
      addToast?.(`Leave applied — ${r.data.days_requested} day(s) requested`, 'success');
      setShowApply(false);
      fetchRequests();
    } catch (e) {
      addToast?.(e.response?.data?.error || 'Failed to apply leave', 'error');
    } finally {
      setApplySaving(false);
    }
  };

  // ─── Approve / Reject ───────────────────────────────────────────────────────
  const openAction = (req, action) => {
    setActionTarget({ req, action });
    setActionRemarks('');
  };

  const submitAction = async () => {
    if (!actionTarget) return;
    setActionSaving(true);
    try {
      await hrApi.put(`${BASE()}/requests/${actionTarget.req.id}/action`, {
        action: actionTarget.action,
        remarks: actionRemarks,
      });
      addToast?.(
        actionTarget.action === 'approve' ? 'Leave approved & attendance marked' : 'Leave rejected',
        actionTarget.action === 'approve' ? 'success' : 'info'
      );
      setActionTarget(null);
      fetchRequests();
    } catch (e) {
      addToast?.(e.response?.data?.error || 'Action failed', 'error');
    } finally {
      setActionSaving(false);
    }
  };

  // ─── Create leave type ──────────────────────────────────────────────────────
  const submitLeaveType = async () => {
    if (!ltForm.name || !ltForm.code) return addToast?.('Name and code required', 'error');
    setLtSaving(true);
    try {
      await hrApi.post(`${BASE()}/types`, ltForm);
      addToast?.('Leave type created', 'success');
      setLtForm({ name: '', code: '', days_allowed: 12, is_paid: true, carry_forward: false });
      setShowLtForm(false);
      fetchLeaveTypes();
    } catch (e) {
      addToast?.(e.response?.data?.error || 'Failed to create', 'error');
    } finally {
      setLtSaving(false);
    }
  };

  // ─── TAB: REQUESTS ──────────────────────────────────────────────────────────
  const renderRequests = () => (
    <div>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {/* Status filter */}
        <div style={{ display: 'flex', gap: 6 }}>
          {['all', 'pending', 'approved', 'rejected'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              style={{
                padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                background: statusFilter === s ? '#4f46e5' : '#f1f5f9',
                color: statusFilter === s ? '#fff' : '#475569',
                border: statusFilter === s ? '2px solid #4f46e5' : '2px solid transparent',
                transition: 'all .15s',
              }}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => fetchRequests()} disabled={reqLoading}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', cursor: reqLoading ? 'not-allowed' : 'pointer', fontSize: 13, opacity: reqLoading ? .6 : 1 }}>
          {reqLoading ? <Spinner size={14} /> : <RefreshCw size={14} />} {reqLoading ? 'Loading…' : 'Refresh'}
        </button>
        <button onClick={openApply} disabled={applySaving}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', borderRadius: 8, background: '#4f46e5', color: '#fff', border: 'none', cursor: applySaving ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 13, opacity: applySaving ? .7 : 1 }}>
          <Plus size={15} /> Apply Leave
        </button>
      </div>

      {/* Info banner */}
      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 16px', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#1e40af' }}>
        <Info size={16} />
        Leave requests are auto-approved and attendance is updated automatically for the selected dates.
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              {['Employee/Worker', 'Type', 'From', 'To', 'Days', 'Reason', 'Status', 'Actions'].map(h => (
                <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: '1px solid #e2e8f0' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {reqLoading ? (
              Array.from({ length: 5 }).map((_, i) => <TableRowSkeleton key={i} cols={8} />)
            ) : requests.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
                  <Calendar size={32} style={{ margin: '0 auto 10px', display: 'block', opacity: .4 }} />
                  No leave requests found
                </td>
              </tr>
            ) : requests.map(r => (
              <tr key={r.id} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background .1s' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <td style={{ padding: '12px 14px' }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>{r.employee_name}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                    {r.entity_type === 'worker' ? 'Worker' : 'Employee'}
                  </div>
                </td>
                <td style={{ padding: '12px 14px' }}>
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                    background: r.is_paid ? '#dbeafe' : '#f1f5f9', color: r.is_paid ? '#1e40af' : '#475569'
                  }}>
                    {r.leave_type_code}
                  </span>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{r.leave_type}</div>
                </td>
                <td style={{ padding: '12px 14px', fontSize: 13, color: '#334155' }}>{r.from_date}</td>
                <td style={{ padding: '12px 14px', fontSize: 13, color: '#334155' }}>{r.to_date}</td>
                <td style={{ padding: '12px 14px', fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{r.days}</td>
                <td style={{ padding: '12px 14px', fontSize: 13, color: '#475569', maxWidth: 180 }}>
                  <span title={r.reason} style={{ overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    {r.reason || '—'}
                  </span>
                </td>
                <td style={{ padding: '12px 14px' }}><StatusBadge status={r.status} /></td>
                <td style={{ padding: '12px 14px' }}>
                  {false ? (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => openAction(r, 'approve')}
                        title="Approve"
                        style={{ padding: '5px 12px', borderRadius: 6, background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0', cursor: 'pointer', fontSize: 12, fontWeight: 600, minHeight: 44 }}>
                        ✓ Approve
                      </button>
                      <button onClick={() => openAction(r, 'reject')}
                        title="Reject"
                        style={{ padding: '5px 10px', borderRadius: 6, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', cursor: 'pointer', fontSize: 12, fontWeight: 600, minHeight: 44 }}>
                        ✗
                      </button>
                    </div>
                  ) : r.status === 'pending' && !canWriteProp ? (
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>Auto-approved</span>
                  ) : (
                    <span style={{ fontSize: 12, color: '#94a3b8' }}>
                      {r.comments || '—'}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ─── TAB: BALANCES ──────────────────────────────────────────────────────────
  const renderBalances = () => (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <label style={{ fontSize: 13, fontWeight: 600, color: '#475569' }}>Year:</label>
        <select value={balYear} onChange={e => setBalYear(Number(e.target.value))}
          style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, color: '#1e293b', background: '#fff' }}>
          {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <button onClick={fetchSummary} disabled={balLoading}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 8, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', cursor: balLoading ? 'not-allowed' : 'pointer', fontSize: 13, opacity: balLoading ? .6 : 1 }}>
          {balLoading ? <Spinner size={13} /> : <RefreshCw size={13} />} {balLoading ? 'Loading…' : 'Load'}
        </button>
        <div style={{ fontSize: 12, color: '#94a3b8', marginLeft: 8 }}>
          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#22c55e', marginRight: 4 }} />Used &lt;50%
          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#f59e0b', marginLeft: 10, marginRight: 4 }} />&lt;80%
          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#ef4444', marginLeft: 10, marginRight: 4 }} />&gt;80%
        </div>
      </div>

      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 600 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={{ padding: '12px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0', position: 'sticky', left: 0, background: '#f8fafc', zIndex: 1 }}>
                Worker / Employee
              </th>
              {balLoading ? (
                <th style={{ padding: '12px 14px', borderBottom: '1px solid #e2e8f0' }}><Shimmer width={200} height={14} /></th>
              ) : leaveTypes.map(lt => (
                <th key={lt.code} style={{ padding: '12px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap' }}>
                  {lt.code} <span style={{ fontWeight: 400, textTransform: 'none', color: '#94a3b8' }}>({lt.days_allowed}d)</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {balLoading ? (
              Array.from({ length: 5 }).map((_, i) => <TableRowSkeleton key={i} cols={leaveTypes.length + 1 || 5} />)
            ) : summary.length === 0 ? (
              <tr>
                <td colSpan={leaveTypes.length + 1} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
                  No workers found for this organisation
                </td>
              </tr>
            ) : summary.map(row => (
              <tr key={row.id} style={{ borderBottom: '1px solid #f1f5f9' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <td style={{ padding: '12px 14px', position: 'sticky', left: 0, background: 'inherit', zIndex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#1e293b' }}>{row.name}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'capitalize' }}>{row.type}</div>
                </td>
                {leaveTypes.map(lt => {
                  const u = row.usage?.[lt.code] || { allocated: lt.days_allowed, used: 0, remaining: lt.days_allowed };
                  return (
                    <td key={lt.code} style={{ padding: '12px 14px' }}>
                      <UsageBar used={u.used} allocated={u.allocated} />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ─── TAB: LEAVE TYPES ───────────────────────────────────────────────────────
  const renderLeaveTypes = () => (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        {canWriteProp && (
          <button onClick={() => setShowLtForm(v => !v)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', borderRadius: 8, background: '#4f46e5', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, minHeight: 44 }}>
            <Plus size={15} /> {showLtForm ? 'Cancel' : 'Add Leave Type'}
          </button>
        )}
        <button onClick={fetchLeaveTypes} disabled={ltLoading}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', cursor: ltLoading ? 'not-allowed' : 'pointer', fontSize: 13, opacity: ltLoading ? .6 : 1 }}>
          {ltLoading ? <Spinner size={14} /> : <RefreshCw size={14} />} {ltLoading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* Add Form */}
      {showLtForm && (
        <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 12, padding: 20, marginBottom: 20 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: '#0369a1', marginBottom: 14 }}>New Leave Type</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 5 }}>Name *</label>
              <input value={ltForm.name} onChange={e => setLtForm(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Casual Leave"
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 5 }}>Code *</label>
              <input value={ltForm.code} onChange={e => setLtForm(p => ({ ...p, code: e.target.value.toUpperCase() }))}
                placeholder="e.g. CL"
                maxLength={6}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 5 }}>Days per Year</label>
              <input type="number" min={0} value={ltForm.days_allowed}
                onChange={e => setLtForm(p => ({ ...p, days_allowed: Number(e.target.value) }))}
                style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, boxSizing: 'border-box' }} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'flex-end' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={ltForm.is_paid} onChange={e => setLtForm(p => ({ ...p, is_paid: e.target.checked }))} />
                Paid Leave
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                <input type="checkbox" checked={ltForm.carry_forward} onChange={e => setLtForm(p => ({ ...p, carry_forward: e.target.checked }))} />
                Carry Forward
              </label>
            </div>
          </div>
          <div style={{ marginTop: 14, display: 'flex', gap: 10 }}>
            <button onClick={submitLeaveType} disabled={ltSaving}
              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '8px 20px', borderRadius: 8, background: '#4f46e5', color: '#fff', border: 'none', cursor: ltSaving ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 13, opacity: ltSaving ? .7 : 1 }}>
              {ltSaving && <Spinner size={14} color="#fff" />}
              {ltSaving ? 'Saving…' : 'Save Leave Type'}
            </button>
            <button onClick={() => setShowLtForm(false)}
              style={{ padding: '8px 14px', borderRadius: 8, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', cursor: 'pointer', fontSize: 13 }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              {['Code', 'Name', 'Days / Year', 'Paid?', 'Carry Forward'].map(h => (
                <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '.05em', borderBottom: '1px solid #e2e8f0' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ltLoading ? (
              Array.from({ length: 4 }).map((_, i) => <TableRowSkeleton key={i} cols={5} />)
            ) : ltypes.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
                  <AlertCircle size={28} style={{ margin: '0 auto 10px', display: 'block', opacity: .4 }} />
                  No leave types configured yet. Click "Add Leave Type" to create one.
                </td>
              </tr>
            ) : ltypes.map(lt => (
              <tr key={lt.id} style={{ borderBottom: '1px solid #f1f5f9' }}
                onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                <td style={{ padding: '12px 14px' }}>
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, color: '#4f46e5', background: '#eef2ff', padding: '2px 8px', borderRadius: 6 }}>
                    {lt.code}
                  </span>
                </td>
                <td style={{ padding: '12px 14px', fontWeight: 600, fontSize: 14, color: '#1e293b' }}>{lt.name}</td>
                <td style={{ padding: '12px 14px', fontSize: 14, color: '#334155', fontWeight: 700 }}>{lt.days_allowed}</td>
                <td style={{ padding: '12px 14px' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: lt.is_paid ? '#166534' : '#dc2626', background: lt.is_paid ? '#dcfce7' : '#fee2e2', padding: '2px 8px', borderRadius: 6 }}>
                    {lt.is_paid ? 'Paid' : 'Unpaid'}
                  </span>
                </td>
                <td style={{ padding: '12px 14px' }}>
                  <span style={{ fontSize: 13, color: lt.carry_forward ? '#166534' : '#94a3b8' }}>
                    {lt.carry_forward ? '✓ Yes' : '— No'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // ─── MODALS ─────────────────────────────────────────────────────────────────
  const renderApplyModal = () => {
    if (!showApply) return null;
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        onClick={e => { if (e.target === e.currentTarget) setShowApply(false); }}>
        <style>{`
          .apply-leave-modal,
          .apply-leave-modal label,
          .apply-leave-modal select,
          .apply-leave-modal option,
          .apply-leave-modal input,
          .apply-leave-modal textarea {
            color: #000000 !important;
            -webkit-text-fill-color: #000000 !important;
            caret-color: #000000 !important;
            text-shadow: none !important;
          }
          .apply-leave-modal select,
          .apply-leave-modal input,
          .apply-leave-modal textarea,
          .apply-leave-modal option {
            background: #ffffff !important;
          }
          .apply-leave-modal select {
            appearance: auto !important;
          }
        `}</style>
        <div className="apply-leave-modal" style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 520, padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,.25)' }}>
          <div style={{ fontWeight: 800, fontSize: 18, color: '#000000', marginBottom: 20 }}>Apply for Leave</div>

          {/* Entity type toggle */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#000000', display: 'block', marginBottom: 6 }}>For</label>
            <div style={{ display: 'flex', gap: 0, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', width: 'fit-content' }}>
              {['employee', 'worker'].map(t => (
                <button key={t} onClick={() => setApplyForm(p => ({ ...p, entity_type: t, employee_id: '', worker_id: '' }))}
                  style={{ padding: '7px 20px', fontSize: 13, fontWeight: 600, cursor: 'pointer', border: 'none',
                    background: applyForm.entity_type === t ? '#4f46e5' : '#fff',
                    color: applyForm.entity_type === t ? '#fff' : '#000000' }}>
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Person select */}
          {applyForm.entity_type === 'employee' ? (
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#000000', display: 'block', marginBottom: 5 }}>Employee *</label>
              <select value={applyForm.employee_id} onChange={e => setApplyForm(p => ({ ...p, employee_id: e.target.value }))}
                style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, color: '#000000', background: '#ffffff', WebkitTextFillColor: '#000000' }}>
                <option value="">— Select Employee —</option>
                {applyEmployees.map(e => (
                  <option key={e.id} value={e.id}>{staffOptionLabel(e)}</option>
                ))}
              </select>
            </div>
          ) : (
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#000000', display: 'block', marginBottom: 5 }}>Worker *</label>
              <select value={applyForm.worker_id} onChange={e => setApplyForm(p => ({ ...p, worker_id: e.target.value }))}
                style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, color: '#000000', background: '#ffffff', WebkitTextFillColor: '#000000' }}>
                <option value="">— Select Worker —</option>
                {applyWorkers.map(w => (
                  <option key={w.id} value={w.id}>{workerOptionLabel(w)}</option>
                ))}
              </select>
            </div>
          )}

          {/* Leave type */}
          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#000000', display: 'block', marginBottom: 5 }}>Leave Type *</label>
            <select value={applyForm.leave_type_id} onChange={e => setApplyForm(p => ({ ...p, leave_type_id: e.target.value }))}
              style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, color: '#000000', background: '#ffffff', WebkitTextFillColor: '#000000' }}>
              <option value="">— Select Leave Type —</option>
              {applyLeaveTypes.map(lt => (
                <option key={lt.id} value={lt.id}>{lt.name} ({lt.code}) — {lt.days_allowed} days/yr</option>
              ))}
            </select>
          </div>

          {/* Dates */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#000000', display: 'block', marginBottom: 5 }}>From Date *</label>
              <input type="date" value={applyForm.from_date} onChange={e => setApplyForm(p => ({ ...p, from_date: e.target.value }))}
                style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, boxSizing: 'border-box', color: '#000000', background: '#ffffff' }} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#000000', display: 'block', marginBottom: 5 }}>To Date *</label>
              <input type="date" value={applyForm.to_date} min={applyForm.from_date}
                onChange={e => setApplyForm(p => ({ ...p, to_date: e.target.value }))}
                style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, boxSizing: 'border-box', color: '#000000', background: '#ffffff' }} />
            </div>
          </div>

          {/* Days preview */}
          {applyForm.from_date && applyForm.to_date && (
            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '8px 14px', marginBottom: 14, fontSize: 13, color: '#0369a1', fontWeight: 600 }}>
              Duration: {Math.max(0, Math.floor((new Date(applyForm.to_date) - new Date(applyForm.from_date)) / 86400000) + 1)} day(s)
            </div>
          )}

          {/* Reason */}
          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#000000', display: 'block', marginBottom: 5 }}>Reason</label>
            <textarea value={applyForm.reason} onChange={e => setApplyForm(p => ({ ...p, reason: e.target.value }))}
              rows={3} placeholder="Optional reason…"
              style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, resize: 'vertical', boxSizing: 'border-box', color: '#000000', background: '#ffffff' }} />
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={() => setShowApply(false)}
              style={{ padding: '9px 18px', borderRadius: 8, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', cursor: 'pointer', fontWeight: 600 }}>
              Cancel
            </button>
            <button onClick={submitApply} disabled={applySaving}
              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 22px', borderRadius: 8, background: '#4f46e5', color: '#fff', border: 'none', cursor: applySaving ? 'not-allowed' : 'pointer', fontWeight: 700, opacity: applySaving ? .7 : 1 }}>
              {applySaving && <Spinner size={15} color="#fff" />}
              {applySaving ? 'Submitting…' : 'Submit Request'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderActionModal = () => {
    if (!actionTarget) return null;
    const isApprove = actionTarget.action === 'approve';
    return (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        onClick={e => { if (e.target === e.currentTarget) setActionTarget(null); }}>
        <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 440, padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,.25)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: isApprove ? '#dcfce7' : '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {isApprove ? <CheckCircle size={22} color="#166534" /> : <XCircle size={22} color="#991b1b" />}
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: 17, color: '#1e293b' }}>
                {isApprove ? 'Approve Leave' : 'Reject Leave'}
              </div>
              <div style={{ fontSize: 13, color: '#64748b' }}>
                {actionTarget.req.employee_name} · {actionTarget.req.days} day(s)
              </div>
            </div>
          </div>

          {isApprove && (
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13, color: '#166534' }}>
              Attendance will be auto-marked as {actionTarget.req.is_paid ? 'Paid Leave' : 'Unpaid Leave'} from {actionTarget.req.from_date} to {actionTarget.req.to_date}.
            </div>
          )}

          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 5 }}>Remarks (optional)</label>
            <textarea value={actionRemarks} onChange={e => setActionRemarks(e.target.value)}
              rows={3} placeholder="Add a comment…"
              style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, resize: 'none', boxSizing: 'border-box' }} />
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={() => setActionTarget(null)}
              style={{ padding: '9px 18px', borderRadius: 8, background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', cursor: 'pointer', fontWeight: 600 }}>
              Cancel
            </button>
            <button onClick={submitAction} disabled={actionSaving}
              style={{
                display: 'flex', alignItems: 'center', gap: 7,
                padding: '9px 22px', borderRadius: 8, border: 'none', cursor: actionSaving ? 'not-allowed' : 'pointer', fontWeight: 700, opacity: actionSaving ? .7 : 1,
                background: isApprove ? '#166534' : '#dc2626', color: '#fff'
              }}>
              {actionSaving && <Spinner size={15} color="#fff" />}
              {actionSaving ? 'Processing…' : isApprove ? 'Confirm Approve' : 'Confirm Reject'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ─── TAB BAR ────────────────────────────────────────────────────────────────
  const TABS = [
    { id: 'requests', label: 'Leave Requests' },
  ];

  return (
    <div style={{ padding: 24 }}>
      {/* Page header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 26, fontWeight: 900, color: '#1e293b', margin: 0 }}>Leave Management</h1>
        <p style={{ fontSize: 14, color: '#64748b', margin: '4px 0 0' }}>
          Create and track leave for all employees and workers
        </p>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e2e8f0', marginBottom: 24 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{
              padding: '10px 22px', fontSize: 14, fontWeight: 600, cursor: 'pointer', background: 'transparent', border: 'none',
              borderBottom: tab === t.id ? '2px solid #4f46e5' : '2px solid transparent',
              color: tab === t.id ? '#4f46e5' : '#64748b',
              marginBottom: -2, transition: 'all .15s',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === 'requests' && renderRequests()}

      {/* Modals */}
      {renderApplyModal()}
      {false && renderActionModal()}
    </div>
  );
}
